import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";
import { PaginatedMeta } from "./audience_helper";

const apiBase = config.api.API_URL;

/** Mensagem leiga do envelope (interceptor rejeita com { message }). */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (
    e &&
    typeof e === "object" &&
    "message" in e &&
    typeof (e as { message: unknown }).message === "string" &&
    (e as { message: string }).message.trim() !== ""
  ) {
    return (e as { message: string }).message;
  }
  return fallback;
}

export type FormFieldSlugs = {
  include_name: boolean;
  include_phone: boolean;
  slugs: string[];
};

export type FormStatsBySource = {
  embed: number;
  landing: number;
  api: number;
  legacy?: number;
};

export type FormStatsSeriesPoint = {
  date: string;
  embed: number;
  landing: number;
  api: number;
  legacy: number;
};

export type FormStatsSeries = {
  days: number;
  points: FormStatsSeriesPoint[];
};

export type FormStats = {
  accepted: number;
  pending_doi: number;
  confirmed: number;
  rejected: number;
  submissions: number;
  conversion_pct: number | null;
  by_source?: FormStatsBySource;
  series?: FormStatsSeries;
};

export type FormStatsSummary = {
  submissions: number;
  confirmed: number;
  conversion_pct: number | null;
  by_source?: Pick<FormStatsBySource, "embed" | "landing" | "api">;
};

export type CaptureForm = {
  id: number;
  name: string;
  public_key: string;
  status: "active" | "paused";
  double_opt_in: boolean;
  thank_you_message: string | null;
  redirect_url: string | null;
  field_slugs: FormFieldSlugs;
  tag_ids: number[];
  stats_summary?: FormStatsSummary | null;
  created_at?: string;
  updated_at?: string;
};

export type FormEmbedPayload = {
  public_key: string;
  public_url: string;
  submit_url: string;
  landing_url: string;
  snippet: string;
};

export type FormDoiPreview = {
  html: string;
  subject: string;
};

export type FormPayload = {
  name: string;
  status?: "active" | "paused";
  double_opt_in?: boolean;
  thank_you_message?: string | null;
  redirect_url?: string | null;
  field_slugs?: FormFieldSlugs;
  tag_ids?: number[];
};

type FormsListResponse = ApiSuccess<CaptureForm[]> & {
  meta: PaginatedMeta;
};

export const getForms = (params?: {
  search?: string;
  status?: string;
  page?: number;
  per_page?: number;
}): Promise<FormsListResponse> => {
  return axios.get(apiBase + "/forms", { params });
};

export const getForm = (id: number): Promise<ApiSuccess<CaptureForm>> => {
  return axios.get(apiBase + "/forms/" + id);
};

export const createForm = (
  payload: FormPayload
): Promise<ApiSuccess<CaptureForm>> => {
  return axios.post(apiBase + "/forms", payload);
};

export const updateForm = (
  id: number,
  payload: Partial<FormPayload>
): Promise<ApiSuccess<CaptureForm>> => {
  return axios.patch(apiBase + "/forms/" + id, payload);
};

export const deleteForm = (id: number): Promise<ApiSuccess<null>> => {
  return axios.delete(apiBase + "/forms/" + id);
};

export const getFormEmbed = (
  id: number
): Promise<ApiSuccess<FormEmbedPayload>> => {
  return axios.get(apiBase + "/forms/" + id + "/embed");
};

export const getFormStats = (
  id: number,
  params?: { days?: 7 | 30 }
): Promise<ApiSuccess<FormStats>> => {
  return axios.get(apiBase + "/forms/" + id + "/stats", { params });
};

export const getFormDoiPreview = (
  id: number
): Promise<ApiSuccess<FormDoiPreview>> => {
  return axios.get(apiBase + "/forms/" + id + "/doi-preview");
};
